import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Calendar from "lucide-react/dist/esm/icons/calendar";
import CheckCircle from "lucide-react/dist/esm/icons/check-circle";
import CircleX from "lucide-react/dist/esm/icons/circle-x";

import { Button } from "@/components/base/Button";

const CLOSE_DELAY_MS = 5000;

export const GoogleCalendarCallbackPage: React.FC = () => {
  const { t } = useTranslation();
  const [hasOpener] = useState(() => !!window.opener);
  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");

  useEffect(() => {
    if (!hasOpener || !!error) {
      return;
    }

    (window.opener as Window).postMessage(
      { type: "google-calendar-connected" },
      "*"
    );

    const timeout = setTimeout(() => {
      window.close();
    }, CLOSE_DELAY_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [hasOpener, error]);

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <Calendar className="h-12 w-12 text-blue-500" />
          {error ? (
            <CircleX className="absolute -right-1 -bottom-1 h-5 w-5 text-red-500" />
          ) : (
            <CheckCircle className="absolute -right-1 -bottom-1 h-5 w-5 text-green-500" />
          )}
        </div>

        <div className="flex flex-col items-center gap-1 text-center">
          {error ? (
            <>
              <h1 className="text-page-title">
                {t("Google Calendar Connection Failed")}
              </h1>
              <p className="text-text text-sm">{error}</p>
            </>
          ) : (
            <>
              <h1 className="text-page-title">
                {t("Google Calendar Connected")}
              </h1>
              <p className="text-text text-sm">
                {t(
                  "Your calendar has been successfully linked to your account."
                )}
              </p>
            </>
          )}
        </div>
      </div>

      {!error && (
        <p className="text-text text-xs">
          {hasOpener
            ? t("You can close this window or it will be closed automatically")
            : t("Google Calendar was authorized")}
        </p>
      )}

      <Button variant="secondary" onClick={() => window.close()}>
        {t("Close")}
      </Button>
    </div>
  );
};
